import { Inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, combineLatest, distinctUntilKeyChanged, map, share } from 'rxjs';

import { ArcRestrictionService } from '../../../../../../../lib/arc/arc-restriction.service';
import { IBetslipRootState } from '../../../../../../base/store/state';
import { quickDepositSelector } from '../../../../../quick-deposit/selector';
import { betslipPlaceButtonStateIsValidSelector } from '../../../../selectors';
import { IPlaceButtonState, PlaceButtonStates } from '../../../../state';
import { IPlaceButtonDisableService, IPlaceButtonStateService, PLACE_BUTTON_STATE_SERVICE } from '../interfaces/place-button-interfaces';

@Injectable()
export class BetslipPlaceButtonDisableService implements IPlaceButtonDisableService {
    isButtonDisabled$: Observable<boolean>;

    constructor(
        private store: Store<IBetslipRootState>,
        private arcRestrictionService: ArcRestrictionService,
        @Inject(PLACE_BUTTON_STATE_SERVICE) placeButtonService: IPlaceButtonStateService,
    ) {
        const placeButtonState$ = placeButtonService.state$;

        const isQuickDepositAvailable$ = this.store.select(quickDepositSelector).pipe(
            distinctUntilKeyChanged('isAvailable'),
            map((state) => state.isAvailable),
        );

        const isQuickDepositOpen$ = this.store.select(quickDepositSelector).pipe(
            distinctUntilKeyChanged('isOpen'),
            map((state) => state.isOpen),
        );
        const isPlaceButtonValid$ = this.store.select(betslipPlaceButtonStateIsValidSelector);
        const isUserOnPlayBreak$ = this.arcRestrictionService.isUserOnPlayBreak$;
        this.isButtonDisabled$ = combineLatest([
            placeButtonState$,
            isUserOnPlayBreak$,
            isQuickDepositAvailable$,
            isPlaceButtonValid$,
            isQuickDepositOpen$,
        ]).pipe(
            map(([state, isUserOnPlayBreak, isQuickDepositAvailable, isPlaceButtonValid, isQuickDepositOpen]) =>
                this.getButtonDisabled(state, isUserOnPlayBreak, isQuickDepositAvailable, isPlaceButtonValid, isQuickDepositOpen),
            ),
            share(),
        );
    }

    private getButtonDisabled(
        state: IPlaceButtonState,
        isUserOnPlayBreak: boolean,
        isQuickDepositAvailable: boolean,
        isButtonValid: boolean,
        isQuickDepositOpen: boolean,
    ): boolean {
        // Todo we should switch from PlaceButtonStates with enum flags to using Fixed states
        // So Login, UpdateStake, ProcessingDeposit instead of (Processing | Deposit) etc

        if (isQuickDepositOpen && !isQuickDepositAvailable) {
            return !isQuickDepositAvailable;
        }

        if (
            state.action === PlaceButtonStates.Login ||
            state.action === PlaceButtonStates.UpdateStake ||
            state.action === PlaceButtonStates.UpdateStakes ||
            // eslint-disable-next-line no-bitwise
            state.action === (PlaceButtonStates.Deposit | PlaceButtonStates.Processing) ||
            state.action === PlaceButtonStates.AcceptChanges ||
            (state.action === PlaceButtonStates.Deposit && isQuickDepositAvailable) ||
            // eslint-disable-next-line no-bitwise
            (state.action === (PlaceButtonStates.PlaceBet | PlaceButtonStates.Deposit) && isQuickDepositAvailable)
        ) {
            return false;
        }

        if (!isButtonValid || isUserOnPlayBreak || state.isProcessing) {
            return true;
        }

        return !state.isValid;
    }
}
